import { LocaleValuesFragment } from 'src/generated/operations';
import { useEffect, useMemo, useState } from 'react';
import { composeExcelBuffer } from 'src/utils/composeExcelBuffer';
import { useBelimoTranslate } from 'src/common/hooks/useBelimoTranslate';
import * as ExcelJS from 'exceljs';

type Link = { text: string; hyperlink: string }

function isLink(value: LocaleValuesFragment | Link | string): value is Link {
    return Boolean(value) && typeof value === 'object' && 'hyperlink' in value;
}

type Props = {
    table: {
        columns: string[];
        rows: Array<(LocaleValuesFragment | Link | string | undefined | null)[]>;
    };
}

export const useComposeExcelFile = ({ table }: Props) => {
    const t = useBelimoTranslate();

    const [href, setHref] = useState<string | null>(null);

    const excelTable = useMemo(() => {
        const columns = table.columns.map((column) => ({
            header: t(column),
            key: t(column),
            width: 15,
        }));

        const rows = table.rows.map(
            (row) => row.map((value) => (value && isLink(value) ? value : t(value))),
        );

        return { columns, rows };
    }, [
        table,
        t,
    ]);

    useEffect(() => {
        let active = true;

        async function generate() {
            setHref(null);

            const { columns, rows } = excelTable;
            const buffer = await composeExcelBuffer(columns, rows as ExcelJS.RowValues[]);

            const newHref = window.URL.createObjectURL(
                new Blob([buffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                }),
            );

            if (!active) {
                return;
            }
            setHref(newHref);
        }

        generate();

        return () => {
            active = false;
        };
    }, [excelTable]);

    return href;
};
